import { Injectable, inject, signal } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import {
  CommunityTranslationMode,
  CommunityTranslationStoredMode,
  communitySameLocale
} from '../models/community.models';
import { CommunityService } from './community.service';

/** What `modeChanges` emits: the language whose mode the viewer just changed, and the new mode. */
export interface CommunityTranslationModeChange {
  sourceLocale: string;
  mode: CommunityTranslationMode;
}

/**
 * The viewer's per-source-language translation preference (§3.1 of
 * docs/community/TRANSLATION.md).
 *
 * Root-provided, because two very different places write to it — the dropdown on
 * a single post and the settings page — and both must see the same state without
 * a reload. It is normally *hydrated* from the community overview
 * (`translation_preferences`), so the thread page needs no extra request; only
 * the settings page, which may be opened directly, fetches the matrix itself.
 *
 * Keys are stored lower-cased for lookup, but a region is part of the identity:
 * 'es-MX' and 'es' are two different source languages with two different rows.
 */
@Injectable({ providedIn: 'root' })
export class CommunityTranslationPreferenceService {
  private readonly community = inject(CommunityService);

  /** Only the persisted modes live here; a language that is absent is `ask`. */
  readonly preferences = signal<Record<string, CommunityTranslationStoredMode>>({});
  /** The language currently being written, for disabling its control. */
  readonly saving = signal<string | null>(null);
  /** Translation key of the last failed write, or null. */
  readonly error = signal<string | null>(null);
  readonly loaded = signal(false);

  private readonly modeChangesSubject = new Subject<CommunityTranslationModeChange>();
  /** Emitted optimistically, i.e. before the server has confirmed the write. */
  readonly modeChanges: Observable<CommunityTranslationModeChange> = this.modeChangesSubject.asObservable();

  /**
   * Fill from the overview's `translation_preferences`. An older backend sends
   * nothing at all, which must not be mistaken for "everything is `ask`" — the
   * matrix is only marked loaded when a payload actually arrived.
   */
  hydrate(preferences: Record<string, CommunityTranslationStoredMode> | null | undefined): void {
    if (!preferences) return;
    this.preferences.set(this.normalize(preferences));
    this.loaded.set(true);
  }

  /** Fetch the matrix; used by the settings page, which can be opened cold. */
  load(force = false): void {
    if (this.loaded() && !force) return;
    this.community.getTranslationPreferences().subscribe({
      next: res => {
        this.preferences.set(this.normalize(res.preferences ?? {}));
        this.loaded.set(true);
      },
      error: () => this.error.set('community.errors.generic')
    });
  }

  modeFor(sourceLocale: string | null | undefined): CommunityTranslationMode {
    if (!sourceLocale) return 'ask';
    return this.preferences()[sourceLocale.toLowerCase()] ?? 'ask';
  }

  /**
   * Whether the dropdown should be offered at all. `never` still shows it — the
   * only way back from `never` is the control itself, so hiding it would make
   * the choice irreversible for that reader.
   */
  shouldOffer(sourceLocale: string | null | undefined, uiLocale: string, available: boolean): boolean {
    if (!available || !sourceLocale) return false;
    return !communitySameLocale(sourceLocale, uiLocale);
  }

  /** Whether this item is translated without being asked. */
  shouldAuto(sourceLocale: string | null | undefined, uiLocale: string, available: boolean): boolean {
    return this.shouldOffer(sourceLocale, uiLocale, available) && this.modeFor(sourceLocale) === 'always';
  }

  /**
   * Write one language's mode. Optimistic: the UI flips at once and rolls back
   * if the request fails, exactly like the notification matrix.
   */
  setMode(sourceLocale: string, mode: CommunityTranslationMode): void {
    const key = sourceLocale.toLowerCase();
    if (this.modeFor(sourceLocale) === mode) return;
    const previous = this.preferences();
    const next = { ...previous };
    if (mode === 'ask') {
      delete next[key];
    } else {
      next[key] = mode;
    }
    this.preferences.set(next);
    this.error.set(null);
    this.saving.set(key);
    this.modeChangesSubject.next({ sourceLocale, mode });

    this.community.updateTranslationPreference(sourceLocale, mode).subscribe({
      next: res => {
        this.saving.set(null);
        this.preferences.set(this.normalize(res.preferences ?? {}));
        this.loaded.set(true);
      },
      error: () => {
        this.saving.set(null);
        this.preferences.set(previous);
        this.error.set('community.errors.generic');
        // Tell listeners what the mode really is again, so a page that started
        // translating on an optimistic `always` can settle back.
        this.modeChangesSubject.next({ sourceLocale, mode: this.modeFor(sourceLocale) });
      }
    });
  }

  private normalize(preferences: Record<string, CommunityTranslationStoredMode>): Record<string, CommunityTranslationStoredMode> {
    const out: Record<string, CommunityTranslationStoredMode> = {};
    Object.entries(preferences || {}).forEach(([locale, mode]) => {
      if (mode === 'always' || mode === 'never') out[locale.toLowerCase()] = mode;
    });
    return out;
  }
}
